import { ChevronLeft, ChevronRight, Share2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog'
import { useNotification } from '../contexts/NotificationContext'
import { cn } from '../lib/utils'

async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// Native share via Capacitor — always sends the rendered image as the
// payload. title/text propagate to the share sheet as caption/metadata.
async function shareNative({ title, text, url, file }) {
  const dataUrl = await blobToDataUrl(file)
  await Share.share({ title, text, url, files: [dataUrl] })
}

// Web share via navigator.share. If the browser can't share files (typical
// on desktop), fall back to downloading the rendered image — the user can
// then upload it wherever they want.
async function shareWeb({ title, text, url, file }) {
  if (typeof navigator !== 'undefined'
    && navigator.canShare
    && navigator.canShare({ files: [file] })
    && navigator.share) {
    await navigator.share({ title, text, url, files: [file] })
    return
  }
  // Fallback: download the image. Lets the user post it manually wherever
  // they wanted to share.
  const objectUrl = URL.createObjectURL(file)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = file.name || 'dub-share.png'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(objectUrl)
}

export default function ShareButton({
  title,
  text,
  url,
  // getImage(variantKey?) => Promise<Blob>. variantKey is passed only when
  // `variants` is provided (the design carousel).
  getImage,
  // Optional design variants: [{ key, label }]. When present, the preview
  // becomes a left/right carousel so owners can compare looks before sharing.
  variants = null,
  variant = 'outline',
  size = 'sm',
  className,
  children,
  iconOnly = false,
  ariaLabel,
}) {
  const [busy, setBusy] = useState(false)
  // Owners wanted a preview of the share-card image before the device's
  // share sheet pops — impossible once the native sheet takes over, so we
  // render the image into a modal first and only invoke the OS share when the
  // user confirms the design they want from there.
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const [previewUrl, setPreviewUrl] = useState(null)
  const currentBlobRef = useRef(null)
  const blobCacheRef = useRef(new Map()) // variant key -> Blob
  const notify = useNotification()

  const hasVariants = Array.isArray(variants) && variants.length > 0
  const variantCount = hasVariants ? variants.length : 1

  // Object URLs leak unless explicitly revoked. Tied to the URL value so each
  // new preview cleans up the previous one, including when the modal closes.
  useEffect(() => {
    if (!previewUrl) return undefined
    return () => URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const reportError = (err) => {
    if (err?.name === 'AbortError') return
    console.error('Share failed:', err)
    notify.error('Unable to open share sheet')
  }

  // Generate (or reuse a cached) blob for the variant at `i`, then show it.
  const showVariant = async (i) => {
    if (!getImage) return
    const key = hasVariants ? variants[i].key : '__single__'
    let blob = blobCacheRef.current.get(key)
    if (!blob) {
      blob = await getImage(hasVariants ? variants[i].key : undefined)
      if (blob) blobCacheRef.current.set(key, blob)
    }
    if (!blob) {
      notify.error('Could not generate share image')
      return
    }
    currentBlobRef.current = blob
    setPreviewUrl(URL.createObjectURL(blob))
  }

  const handleClick = async () => {
    if (busy) return
    setBusy(true)
    try {
      blobCacheRef.current.clear()
      currentBlobRef.current = null
      setIndex(0)
      setOpen(true)
      await showVariant(0)
    } catch (err) {
      reportError(err)
      setOpen(false)
    } finally {
      setBusy(false)
    }
  }

  const goTo = async (nextIndex) => {
    if (busy || nextIndex === index || nextIndex < 0 || nextIndex >= variantCount) return
    setBusy(true)
    setIndex(nextIndex)
    try {
      await showVariant(nextIndex)
    } catch (err) {
      reportError(err)
    } finally {
      setBusy(false)
    }
  }

  const handleConfirmShare = async () => {
    const blob = currentBlobRef.current
    if (!blob || busy) return
    setBusy(true)
    try {
      const file = new File([blob], 'dub-share.png', { type: blob.type || 'image/png' })
      if (Capacitor.isNativePlatform()) {
        await shareNative({ title, text, url, file })
      } else {
        await shareWeb({ title, text, url, file })
      }
      closePreview()
    } catch (err) {
      reportError(err)
    } finally {
      setBusy(false)
    }
  }

  const closePreview = () => {
    if (busy) return
    setOpen(false)
    setPreviewUrl(null)
    currentBlobRef.current = null
    blobCacheRef.current.clear()
  }

  const currentLabel = hasVariants ? variants[index]?.label : null

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleClick}
        disabled={busy}
        className={className}
        type="button"
        aria-label={iconOnly ? (ariaLabel || 'Share') : undefined}
      >
        <Share2 className={cn('h-4 w-4', !iconOnly && 'mr-2')} />
        {!iconOnly && (children || 'Share')}
      </Button>

      <Dialog open={open} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-lg sm:max-w-2xl p-4 sm:p-6 gap-3">
          <DialogHeader>
            <DialogTitle>Preview share</DialogTitle>
          </DialogHeader>

          {/* Full-width preview; navigation lives in a row beneath it so the
              arrows never cover the image. */}
          <div className="flex justify-center">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={currentLabel ? `Share preview — ${currentLabel}` : 'Share preview'}
                className="w-full h-auto max-h-[72vh] object-contain rounded-2xl"
              />
            ) : (
              <div className="aspect-square w-full flex items-center justify-center text-muted-foreground text-sm">
                Generating…
              </div>
            )}
          </div>

          {hasVariants && (
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="icon"
                type="button"
                onClick={() => goTo(index - 1)}
                disabled={busy || index === 0}
                aria-label="Previous design"
                className="shrink-0 rounded-full"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <span className="min-w-24 text-center text-sm font-medium">{currentLabel}</span>
              <Button
                variant="outline"
                size="icon"
                type="button"
                onClick={() => goTo(index + 1)}
                disabled={busy || index === variantCount - 1}
                aria-label="Next design"
                className="shrink-0 rounded-full"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          )}

          {hasVariants && (
            <div className="flex flex-col items-center gap-2">
              <p className="text-xs text-muted-foreground">{index + 1} of {variantCount}</p>
              <div className="flex items-center gap-1.5">
                {variants.map((v, i) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-label={`Show ${v.label} design`}
                    className={cn(
                      'rounded-full transition-all duration-200',
                      i === index ? 'w-4 h-1.5 bg-primary' : 'w-1.5 h-1.5 bg-muted-foreground/40',
                    )}
                  />
                ))}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={closePreview} disabled={busy} type="button">
              Cancel
            </Button>
            <Button onClick={handleConfirmShare} disabled={busy || !previewUrl} type="button">
              <Share2 className="h-4 w-4 mr-2" />
              {busy ? 'Working…' : 'Share this one'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
